import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  Output,
  ViewChild,
  EventEmitter,
  Input,
  AfterViewInit,
  OnDestroy,
  Inject,
  computed,
  inject
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { faFilm, faUser } from '@fortawesome/free-solid-svg-icons';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { filter, takeUntil } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { Channel, SearchItemType } from '@usheru-hff/shared/domain';
import { SharedUtilsTranslationModule } from '@usheru-hff/shared/utils-translation';

import { SearchFiltersDropdownComponent } from '../search-filters-dropdown/search-filters-dropdown.component';
import { SearchSuggestionItemComponent } from '../search-suggestion-item/search-suggestion-item.component';
import { SearchMovieService } from '../shared/search-movie.service';

/**
 * Component to search and redirect to movie or talent pages.
 *
 * //TODO not finished
 * //TODO barbeitoalex propostal - remove .spec.ts
 * //TODO barbeitoalex propostal - is too confuse the {@link mode} {@link autocomplete} implications.
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - use environment values in their own method and subscribe to environment
 * //TODO barbeitoalex propostal - update translate text with an observable to the current language changes
 */
@Component({
  selector: 'ush-search-movie',
  templateUrl: './search-movie.component.html',
  styleUrls: ['./search-movie.component.scss'],
  providers: [SearchMovieService],
  standalone: true,
  imports: [
    CommonModule,
    AutoCompleteModule,
    FormsModule,
    ReactiveFormsModule,
    IconFieldModule,
    InputIconModule,
    SearchFiltersDropdownComponent,
    SearchSuggestionItemComponent,
    SharedUtilsTranslationModule
  ]
})
export class SearchMovieComponent implements OnInit, AfterViewInit, OnDestroy {
  searchMovieService: SearchMovieService = inject(SearchMovieService);

  /**
   * //TODO docs
   */
  @ViewChild('autocomplete_movie', { read: AutoComplete })
  autocomplete: AutoComplete;

  /**
   * @deprecard Don't found any use
   */
  @ViewChild('searchItem')
  searchItemRef: ElementRef;

  @ViewChild('redirectionSearch')
  redirectionSearchRef: ElementRef;

  redirectionUrl = '/movies/watch-at-home';

  /**
   * @docsNotRequired
   */
  @Input()
  classStyle!: string;

  @Input()
  redirect: boolean = true;

  /**
   * Status to know if it is collapsable.
   */
  active = false;

  /**
   * @docsNotRequired
   */
  channel: Channel;

  /**
   * @docsNotRequired
   */
  searchSuggestions = this.searchMovieService.results;

  /**
   * Value selected from the the autocomplete. (Movie or Star)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;

  /**
   * Placeholder text for the autocomplete.
   */
  placeholder = computed(() => this.searchMovieService.filter().placeholderTranslationKey);

  /**
   * Status to know if can search persons too. Activates from {@link Environment} talentPages.
   */
  canSearchTalent = false;

  /**
   * Disables the search and reditects to a catalog page (watch-at-home right now).
   *
   * //TODO barbeitoalex - change this to a string and use that value for the redirection.
   */
  searchComponentRedirect: boolean;

  $routeSubscription = null;

  //
  /**
   * To use as custom component and not a header elements. Sets the "mobile {@link mode}."
   */
  @Input()
  asComponent = false;

  // Desktop mode can compress the mobile one no.
  /**
   * //TODO docs
   */
  @Input()
  mode: 'desktop' | 'mobile' = 'desktop';

  /**
   * //TODO docs
   */
  @Input()
  searchActive: boolean;

  /**
   * //TODO docs
   */
  @Input()
  searchActiveMobile: boolean;

  /**
   * Notify when a item is selected but don't emit any value.
   *
   * //TODO barbeitoalex propostal - refractor name. Not only movies could be selected.
   */
  @Output()
  itemSelect = new EventEmitter<SearchItemType>();

  /**
   * @docsNotRequired
   */
  faUser = faUser;

  /**
   * @docsNotRequired
   */
  faFilm = faFilm;

  readonly searchFilmAndCelebsPlaceholder = $localize`:@@public.filters.movieCast.placeholder:Search Film Title, Actor, Director`;

  withFilters: boolean = false;

  /**
   * Used to avoid close the active status in the open event.
   */
  onBlurCancel = false;

  destroySubs = new EventEmitter();

  isLoading$ = toObservable(this.searchMovieService.isLoading);
  suggestions$ = toObservable(this.searchMovieService.results);

  selectedItem: SearchItemType;

  constructor(
    @Inject(ENVIRONMENT) private env: Environment,
    private router: Router,
    private channelService: ChannelService
  ) {}

  ngOnInit(): void {
    this.channel = this.channelService.channel;
    this.canSearchTalent = this.env?.customPageParams?.talentPages;
    this.searchComponentRedirect = this.env?.searchComponentRedirect;
    this.searchActive = this.env?.customPageParams?.headerSearchActive;
    this.searchActiveMobile = this.env?.customPageParams?.headerSMsearchActive;
    this.withFilters = this.env?.searchMovieWidgetConfig?.withFilters || false;

    if (this.asComponent) this.mode = 'mobile';
    if (!this.withFilters && this.canSearchTalent) {
      this.searchMovieService.setPlaceholder(this.searchFilmAndCelebsPlaceholder);
    }
    this.searchMovieService.setIsRedirectEnabled(this.redirect);

    this.isLoading$.pipe(takeUntil(this.destroySubs)).subscribe(loading => {
      this.autocomplete.loading = loading;
    });
    this.suggestions$.pipe(takeUntil(this.destroySubs), filter(Boolean)).subscribe(() => {
      this.autocomplete.show(true);
    });
  }

  ngAfterViewInit(): void {
    if (this.searchComponentRedirect) {
      this.$routeSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(() => {
          const isMoviesPage = location.pathname.startsWith('/movies');
          const style = this.redirectionSearchRef.nativeElement.style;
          style.opacity = isMoviesPage ? 0.5 : 1;
          style.pointerEvents = isMoviesPage ? 'none' : 'auto';
        });
    }
  }

  loadSuggestion(event) {
    // Preserve the filters when searching
    this.searchMovieService.resetState(this.searchMovieService.filter());

    const keyword = event.query.trim();
    this.searchMovieService.setKeyword(keyword);
    // with filters currently available for Panoptico
    if (this.withFilters) {
      this.searchMovieService.searchResults();
      return;
    }

    this.searchMovieService.searchMovies();
  }

  onSelect(event) {
    this.selectedItem = event?.value;
    this.handleItemSelect(event?.value);
  }

  onKeyup(event) {
    if (event.keyCode === 13 && this.value && this.value.length > 0) {
      this.loadSuggestion({ query: this.value });
    }
  }

  onBlur(): void {
    setTimeout(() => {
      if (this.mode == 'desktop') {
        if (!this.onBlurCancel) {
          this.closeSearchBar();
          this.searchMovieService.setKeyword('');
        }
      }
    }, 200);
  }

  onClear(): void {
    this.searchMovieService.setKeyword('');
  }

  onIconClick() {
    if (!this.getSearchActive()) {
      this.toggleSearchBar();
    }
  }

  toggleSearchBar(/*value?: boolean*/) {
    this.active = !this.active;
    if (this.active) {
      this.autocomplete.el.nativeElement.querySelector('.p-autocomplete-input').focus();
      this.onBlurCancel = true;
      // prevent trigger close the search bar in the open animation
      setTimeout(() => {
        this.autocomplete.el.nativeElement.querySelector('.p-autocomplete-input').focus();
        this.onBlurCancel = false;
      }, 990);
    }
  }

  closeSearchBar() {
    this.autocomplete.hide();
    this.autocomplete.clear();
    this.active = false;
  }

  getSearchActive() {
    return this.mode == 'desktop' ? this.searchActive : this.searchActiveMobile;
  }

  handleItemSelect(event: any) {
    this.itemSelect.emit(event);
  }

  get isSearchCompactedEnabled() {
    return this.env?.searchMovieWidgetConfig?.searchCompacted;
  }

  get searchIconPosition() {
    return this.env?.searchMovieWidgetConfig?.searchIconPosition || 'left';
  }
  get searchIcon() {
    return this.env?.searchMovieWidgetConfig?.searchIcon || 'pi-search';
  }

  get closeIcon() {
    return 'pi-times';
  }

  get isLoading() {
    return this.autocomplete?.loading;
  }

  ngOnDestroy(): void {
    if (this.$routeSubscription) {
      this.$routeSubscription.unsubscribe();
    }
    this.destroySubs.next(true);
    this.destroySubs.complete();
  }
}
